/**
 * Permission mapping configuration
 * Maps page routes and API routes to permission slugs
 */

import type { PermissionMapping } from './permissions-types';
import { PAGE_PERMISSIONS } from './permissions-pages';
import { CORE_API_PERMISSIONS } from './permissions-api-core';
import { ADMIN_API_PERMISSIONS } from './permissions-api-admin';
import { ACCOUNT_API_PERMISSIONS } from './permissions-api-account';

export type { PermissionConfig, PermissionMapping } from './permissions-types';
export { PAGE_PERMISSIONS } from './permissions-pages';

// API route permissions (order matters: getApiPermission takes the first match)
export const API_PERMISSIONS: PermissionMapping[] = [
  ...CORE_API_PERMISSIONS,
  ...ADMIN_API_PERMISSIONS,
  ...ACCOUNT_API_PERMISSIONS,
];

/**
 * Get permission for a page route
 */
export function getPagePermission(path: string): string | null | undefined {
  // Exact match first
  const exact = PAGE_PERMISSIONS.find((p) => p.path === path);
  if (exact) return exact.permission;

  // Pattern match for dynamic routes
  for (const mapping of PAGE_PERMISSIONS) {
    if (mapping.path.includes('[')) {
      const pattern = mapping.path.replace(/\[.*?\]/g, '[^/]+');
      const regex = new RegExp(`^${pattern}$`);
      if (regex.test(path)) {
        return mapping.permission;
      }
    }
  }

  return null;
}

/**
 * Get permission for an API route
 */
export function getApiPermission(path: string): string | null | undefined {
  // Exact match first
  const exact = API_PERMISSIONS.find((p) => p.path === path);
  if (exact) return exact.permission;

  // Pattern match for dynamic routes
  for (const mapping of API_PERMISSIONS) {
    if (mapping.path.includes('[')) {
      const pattern = mapping.path.replace(/\[.*?\]/g, '[^/]+');
      const regex = new RegExp(`^${pattern}$`);
      if (regex.test(path)) {
        return mapping.permission;
      }
    }
  }

  return null;
}

/**
 * Get all permissions
 */
export function getAllPermissions(): PermissionMapping[] {
  return [...PAGE_PERMISSIONS, ...API_PERMISSIONS];
}
